import { useState, useCallback } from 'react';
import client from '../api/client';
import { useToast } from '../context/ToastContext';
import { clearScheduleOrder } from '../utils/scheduleOrderStorage';
import DeleteAllConfirmModal from '../components/DeleteAllConfirmModal';

/**
 * Массовое удаление строк графика через /schedules/bulk + модальное подтверждение.
 */
export function useDeleteAllSchedules() {
  const { showSuccess, showError, showInfo } = useToast();
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [pending, setPending] = useState(null);

  const requestDeleteAll = useCallback(
    ({
      total,
      cityName,
      scheduleType,
      cityId,
      scheduleLabel,
      orderStorageKey,
      onSuccess,
    }) => {
      if (!cityId) {
        showInfo('Пожалуйста, выберите объект');
        return;
      }
      if (!total || total <= 0) {
        showInfo('Нет данных для удаления');
        return;
      }
      setPending({
        total,
        cityName,
        scheduleType,
        cityId: Number(cityId),
        scheduleLabel: scheduleLabel || 'графика',
        orderStorageKey: orderStorageKey || scheduleType,
        onSuccess,
      });
      setOpen(true);
    },
    [showInfo]
  );

  const close = useCallback(() => {
    if (!loading) setOpen(false);
  }, [loading]);

  const confirmDelete = useCallback(async () => {
    if (!pending) return;
    setLoading(true);
    try {
      const { data } = await client.delete('/schedules/bulk', {
        params: {
          schedule_type: pending.scheduleType,
          city_id: pending.cityId,
        },
      });
      clearScheduleOrder(pending.cityId, pending.orderStorageKey);
      pending.onSuccess?.();
      const deleted = data?.deleted ?? pending.total;
      showSuccess(
        `Удалено ${deleted} записей для объекта «${pending.cityName}»`
      );
      setOpen(false);
      setPending(null);
    } catch (error) {
      const detail = error.response?.data?.detail;
      showError(typeof detail === 'string' ? detail : 'Ошибка при удалении');
    } finally {
      setLoading(false);
    }
  }, [pending, showSuccess, showError]);

  const modal = (
    <DeleteAllConfirmModal
      open={open}
      onClose={close}
      onConfirm={confirmDelete}
      total={pending?.total ?? 0}
      cityName={pending?.cityName ?? ''}
      scheduleLabel={pending?.scheduleLabel ?? 'графика'}
      loading={loading}
    />
  );

  return { requestDeleteAll, deleteAllModal: modal };
}
